/**
 * useAssets — OutpostForge frontend (CES standard)
 * Assets of an Outpost plus running instances grouped per asset (one
 * instances call per Outpost; a failure degrades to instancesByAsset=null
 * so cards show "instance data unavailable" without breaking the view).
 */

import { useCallback, useEffect, useState } from 'react';
import { outpostRepository, type OutpostRepository } from '../repositories/OutpostRepository';
import { describeApiError } from '../services/apiErrors';
import type { AssetSummary, InstanceSummary } from '../types/outposts';

export function useAssets(
  outpostId: string | undefined,
  repository: OutpostRepository = outpostRepository,
) {
  const [assets, setAssets] = useState<AssetSummary[]>([]);
  const [instancesByAsset, setInstancesByAsset] = useState<Map<
    string,
    InstanceSummary[]
  > | null>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!outpostId) return;
    setLoading(true);
    setError(null);
    try {
      const [assetsResult, instancesResult] = await Promise.all([
        repository.getAssets(outpostId),
        repository.getInstancesByAsset(outpostId).catch(() => null),
      ]);
      setAssets(assetsResult);
      setInstancesByAsset(instancesResult);
    } catch (err) {
      setError(describeApiError(err));
      setAssets([]);
    } finally {
      setLoading(false);
    }
  }, [outpostId, repository]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { assets, instancesByAsset, loading, error, reload };
}
